/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { useMemo, useState } from "react";
import { cx } from "@panda/css";
import { ButtonList, CosmosButtonProps } from "@components/elements";
import { defaultButtonStyles } from "./button.styles";

type TabbedButtonListProps = {
	onTabChange: (i: number) => void;
	tabs: { tabLabel?: string | null }[];
	className?: string;
	buttonStyles?: string;
	defaultIndex?: number;
	analyticsEventValue?: any;
	tabToggle?: boolean;
	secondaryButtonVariant?: CosmosButtonProps["variant"];
	activeButtonVariant?: CosmosButtonProps["variant"];
	buttonSize?: CosmosButtonProps["size"];
};

export const TabbedButtonList = ({
	onTabChange,
	tabs,
	className,
	buttonStyles,
	defaultIndex,
	analyticsEventValue,
	tabToggle = false,
	secondaryButtonVariant = "secondary",
	activeButtonVariant = "contrast",
	buttonSize = "md",
}: TabbedButtonListProps): React.JSX.Element | null => {
	const [activeIndex, setActiveIndex] = useState(defaultIndex || 0);

	const processedTabs: CosmosButtonProps[] = useMemo(
		() =>
			tabs?.map((tab, i) => {
				return {
					size: buttonSize,
					label: tab.tabLabel,
					variant: "secondary",
					onClick: () => {
						setActiveIndex((currentIndex) => {
							if (!tabToggle || currentIndex !== i) return i;
							return -1;
						});
						onTabChange(i);
					},
					analytics: {
						eventCategory: "button",
						interactionType: "click",
						eventAction: "select",
						eventLabel: tab.tabLabel || "",
						eventValue: analyticsEventValue || {
							component: "TabbedButtonList",
							section: "section",
						},
					},
					className: defaultButtonStyles,
				};
			}) || [],
		[tabs, buttonSize, analyticsEventValue, onTabChange, tabToggle],
	);

	const tabButtons: CosmosButtonProps[] = useMemo(() => {
		return processedTabs?.map((tab, i) => {
			const variant = activeIndex === i ? activeButtonVariant : secondaryButtonVariant;
			const className = cx(
				"tab-button",
				activeIndex === i ? "tab-button-active" : "",
				buttonStyles || defaultButtonStyles,
				variant,
			);
			return { ...tab, variant, className };
		});
	}, [activeButtonVariant, activeIndex, buttonStyles, processedTabs, secondaryButtonVariant]);

	const enableTabs = tabs?.length > 1;

	if (!enableTabs) return null;

	return (
		<div className={cx("tabs", className)}>
			<ButtonList buttons={tabButtons} />
		</div>
	);
};
